import {useEffect, useRef} from 'react';
import {useLocation} from 'react-router';
import {API_BASE} from '../utils/api/reports';
import {getAwpClientHeaders} from '../session/awpRequestHeaders';
import {getOrCreateSession} from '../session/awpSessionContext';
import {getUser} from '../auth/jwtAuth';

const HEARTBEAT_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes
const EVENTS_URL = `${API_BASE}/analytics/events`;

/**
 * Best-effort POST of a single user activity event to dash-api. Failures are swallowed —
 * activity tracking must never disrupt the user experience.
 *
 * @param {object} event { eventType, reportId?, pagePath?, durationMs?, objectId?, objectType?, metadataJson? }
 * @param {boolean} [useBeacon] use navigator.sendBeacon (for unload events)
 */
function postActivityEvent(event, useBeacon = false) {
    try {
        // Skip silently if not yet authenticated — avoids 401 on the post-login redirect
        // before the token has been persisted to localStorage.
        const headers = getAwpClientHeaders();
        if (!headers['Authorization']) return;

        const body = JSON.stringify(event);

        // sendBeacon cannot set Authorization headers, so we always use fetch.
        // For unload events we use keepalive so the request survives page navigation.
        fetch(EVENTS_URL, {
            method: 'POST',
            headers: {'Content-Type': 'application/json', ...headers},
            body,
            keepalive: useBeacon,
        }).catch(() => {});
    } catch (_) {
        /* never throw from tracking */
    }
}

/**
 * Standalone report open/close trackers (no global listeners). Safe to call from any component.
 */
export function trackReportOpen(reportId) {
    if (!reportId) return;
    postActivityEvent({eventType: 'report_open', reportId, durationMs: 0});
}

export function trackReportClose(reportId, durationMs) {
    if (!reportId) return;
    postActivityEvent({eventType: 'report_close', reportId, durationMs});
}

/**
 * Client-side user activity tracking.
 *
 * Mount once near the app root (inside the Router). It will:
 *  - fire `page_view` on every route change
 *  - fire `session_heartbeat` every 5 minutes while the tab is active
 *  - fire `session_end` (with total session duration) on `beforeunload`
 *
 * Server-side events (file uploads, searches, ICR/report/whiteboard creation) are captured
 * automatically by the backend `UserActivityFilter` and require no client call.
 *
 * For report open/close timing, use the standalone {@link trackReportOpen} / {@link trackReportClose}.
 */
export function useActivityTracking() {
    const location = useLocation();
    const sessionStartMsRef = useRef(null);

    // page_view on route change
    useEffect(() => {
        postActivityEvent({eventType: 'page_view', pagePath: location.pathname});
    }, [location.pathname]);

    // heartbeat + session_end (mount-once)
    useEffect(() => {
        const user = getUser();
        const {sessionStart} = getOrCreateSession(user?.uid);
        const startMs = sessionStart ? Date.parse(sessionStart) : Date.now();
        sessionStartMsRef.current = Number.isNaN(startMs) ? Date.now() : startMs;

        const heartbeat = setInterval(() => {
            if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
                return;
            }
            postActivityEvent({eventType: 'session_heartbeat'});
        }, HEARTBEAT_INTERVAL_MS);

        const handleBeforeUnload = () => {
            const durationMs = sessionStartMsRef.current
                ? Date.now() - sessionStartMsRef.current
                : null;
            postActivityEvent({eventType: 'session_end', durationMs}, true);
        };

        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => {
            clearInterval(heartbeat);
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, []);

    return {trackReportOpen, trackReportClose};
}

export {postActivityEvent};
